import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import { generateContent } from '../services/api';
import { useProfile } from '../context/ProfileContext';

const JourneyBuilder = () => {
  const { getSignature, isLimitReached, incrementUsage } = useProfile();
  const [formData, setFormData] = useState({ sailDate: '', detail1: '', detail2: '', detail3: '' });
  const [loading, setLoading] = useState(false);
  const [journey, setJourney] = useState([]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (isLimitReached) return;

    setLoading(true);
    setJourney([]);
    try {
      const generatedJourney = await generateContent('journey', formData);
      const signature = getSignature();
      
      if (Array.isArray(generatedJourney)) {
        const journeyWithSignature = generatedJourney.map(step => ({
          ...step,
          message: step.message + signature
        }));
        setJourney(journeyWithSignature);
        incrementUsage();
      } else {
        throw new Error("Invalid format received");
      }
    } catch (error) {
      console.error(error);
      alert("Failed to generate journey. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h2 className="text-xl font-semibold text-slate-800 mb-6 flex items-center gap-2">
          <SafeIcon name="Calendar" className="text-blue-600" /> Journey Parameters
        </h2>
        <form onSubmit={handleGenerate} className="flex flex-col md:flex-row gap-4 items-end">
          <div className="w-full md:w-1/4">
            <label className="block text-sm font-medium text-slate-700 mb-1">Sail Date</label>
            <input type="date" name="sailDate" required value={formData.sailDate} onChange={handleChange} className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white" />
          </div>
          <div className="w-full md:w-1/4">
            <label className="block text-sm font-medium text-slate-700 mb-1">Client Detail 1</label>
            <input type="text" name="detail1" value={formData.detail1} onChange={handleChange} placeholder="e.g., First time cruiser" className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
          </div>
          <div className="w-full md:w-1/4">
            <label className="block text-sm font-medium text-slate-700 mb-1">Client Detail 2</label>
            <input type="text" name="detail2" value={formData.detail2} onChange={handleChange} placeholder="e.g., Anniversary trip" className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
          </div>
          <div className="w-full md:w-1/4">
            <label className="block text-sm font-medium text-slate-700 mb-1">Client Detail 3</label>
            <input type="text" name="detail3" value={formData.detail3} onChange={handleChange} placeholder="e.g., Loves fine dining" className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
          </div>
          <button 
            type="submit" 
            disabled={loading || !formData.sailDate || isLimitReached} 
            className="w-full md:w-auto px-6 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed whitespace-nowrap" 
          >
            {loading ? <SafeIcon name="Loader" className="animate-spin" /> : <SafeIcon name="Map" />}
            {isLimitReached ? 'Limit Reached' : 'Build Journey'}
          </button>
        </form>
      </div>

      <div className="bg-slate-50 rounded-xl border border-slate-200 p-6 min-h-[400px]">
        <h2 className="text-xl font-semibold text-slate-800 flex items-center gap-2 mb-6">
          <SafeIcon name="Clock" className="text-blue-600" /> Touchpoint Timeline
        </h2>
        {loading ? (
          <div className="h-64 flex flex-col items-center justify-center text-slate-400 space-y-4">
            <SafeIcon name="Compass" className="w-10 h-10 animate-spin-slow text-blue-400" />
            <p>Charting the optimal communication course...</p>
          </div>
        ) : journey.length > 0 ? (
          <div className="relative border-l-2 border-blue-200 ml-4 md:ml-6 space-y-8 pb-4">
            {journey.map((step, index) => (
              <div key={index} className="relative pl-8 md:pl-10">
                <div className="absolute -left-[11px] top-1 w-5 h-5 rounded-full bg-white border-4 border-blue-500 shadow-sm"></div>
                <div className="bg-white rounded-lg p-5 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-3 gap-2">
                    <h3 className="font-semibold text-lg text-slate-800">{step.title}</h3>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium border border-blue-100 w-fit">
                      <SafeIcon name="Calendar" /> {step.timing}
                    </span>
                  </div>
                  <p className="text-slate-600 leading-relaxed text-sm md:text-base whitespace-pre-wrap">
                    {step.message}
                  </p>
                  <div className="mt-4 pt-4 border-t border-slate-100 flex justify-end">
                    <button onClick={() => navigator.clipboard.writeText(step.message)} className="text-xs flex items-center gap-1 text-slate-500 hover:text-blue-600 transition-colors" >
                      <SafeIcon name="Copy" /> Copy Message
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="h-64 flex items-center justify-center text-slate-400 text-center px-8 border-2 border-dashed border-slate-200 rounded-xl bg-white">
            {isLimitReached ? 'You have reached your limit of 10 free generations.' : 'Enter a sail date and client details above to generate a perfectly timed communication calendar.'}
          </div>
        )}
      </div>
    </div>
  );
};

export default JourneyBuilder;